import { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import Container from '~/components/atoms/Container/Container'
import type { CtaProps } from '~/components/atoms/Ctas/Ctas'
import Ctas from '~/components/atoms/Ctas/Ctas'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './FixedBackgroundText.module.css'

export interface FixedBackgroundTextProps {
  pretitle?: string
  title: string
  summary: string
  desktopImage: ImageProps
  mobileImage: ImageProps
  ctas?: CtaProps[]
  testID?: string
}

export default function FixedBackgroundText({
  pretitle,
  title,
  summary,
  desktopImage,
  mobileImage,
  ctas,
  testID,
}: FixedBackgroundTextProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const [ref, inView] = useInView()
  const backgroundRef = useRef<HTMLDivElement>(null)
  const [backgroundPercent, setBackgroundPercent] = useState<number>()

  useEffect(() => {
    const scrollHandler = () => {
      if (inView) {
        const backgroundEl = backgroundRef.current
        if (!backgroundEl) return
        const windowHeight = window.innerHeight
        const viewportOffset = backgroundEl.getBoundingClientRect()
        const top = viewportOffset.top
        const percentage = Math.ceil((top / windowHeight) * 100 - 100) * 0.8
        setBackgroundPercent(percentage)
      }
    }

    window.addEventListener('scroll', scrollHandler)

    return () => {
      window.removeEventListener('scroll', scrollHandler)
    }
  }, [backgroundRef, inView, setBackgroundPercent])

  return (
    <div className={styles.base} data-testid={testID} ref={ref}>
      <div className={styles.background} />
      <Fixture verticalPadding>
        <Container>
          <div
            className={styles.media}
            style={{
              transform: `translateY(${backgroundPercent}%)`,
            }}
            ref={backgroundRef}
          >
            {isMobile ? (
              <Image {...mobileImage} alt="MyPortfolio - MyArtBroker" />
            ) : (
              <Image {...desktopImage} alt="MyPortfolio - MyArtBroker" />
            )}
          </div>
          <div className={styles.content}>
            {pretitle && (
              <Text
                size={TextSizes.primaryTitleMed}
                Tag="p"
                className={styles.pretitle}
              >
                {pretitle}
              </Text>
            )}
            <Text
              size={TextSizes.primaryTitleLrgPlus}
              Tag="h3"
              className={styles.title}
            >
              {title}
            </Text>
            <Text size={TextSizes.bodyMed} Tag="p" className={styles.summary}>
              {summary}
            </Text>
            {ctas && (
              <div className={styles.ctas}>
                <Ctas ctas={ctas} center={true} stack={false} hideIcon={true} />
              </div>
            )}
          </div>

          {/* <Grid>
            <Row className={styles.alignCenter}>
              <Col variant="col-1/2--md" cssClass={styles.largeGutterRight}>
                &nbsp;
              </Col>
              <Col
                variant="col-1/2--md"
                cssClass={styles.largeGutterLeft}
              ></Col>
            </Row>
          </Grid> */}
        </Container>
      </Fixture>
    </div>
  )
}
